import { telemetry } from "telemetry";
import { log } from "utils/log";

import { minimapFilePath } from "./offlineStore";
import { buildTileSetFileUrl, parseTileSetManifest, TILE_MANIFEST_RELATIVE_PATH } from "./tiledSource";
import { allTileSetFilePaths, type TiledDrawingSource } from "./tileMath";

const l = log.miniMap.tiled;

/** Concurrent download cap, matching the online tile fetch's own budget (`tileBitmapCache.ts`). */
const MAX_CONCURRENT_DOWNLOADS = 8;
const RETRY_BACKOFF_MS = [1000, 4000];

/** The slice of `OfflineScene` this module needs — matches `@novorender/api`'s public surface. */
export type MinimapFullSyncScene = {
    readonly id: string;
    readonly dir: {
        getJournalEntries(): Promise<IterableIterator<{ name: string; size: number }>>;
        read(name: string): Promise<ArrayBuffer | undefined>;
        write(name: string, buffer: ArrayBuffer): Promise<void>;
    };
};

/**
 * Resolve a drawing's tile-set root URL, or `null` when it has no completed
 * import yet. `forceRefresh` mints a fresh signed prefix instead of reusing a
 * cached one (same contract as `createBimSceneFileSignedDownloadUrl`) --
 * needed because a full sync's hundreds of tiles across 8 workers, running
 * after geometry sync already completed, are the case most likely to outlive
 * a FAS signature's lifetime.
 */
export type ResolveMinimapBaseUrl = (
    fileInstanceId: string,
    options?: { forceRefresh?: boolean },
) => Promise<string | null>;

/** One tile-set file still to download, as handed to a worker. */
type DownloadItem = { fileInstanceId: string; relativePath: string; baseUrl: string };

function delay(ms: number, abortSignal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, ms);
        abortSignal.addEventListener(
            "abort",
            () => {
                clearTimeout(timer);
                reject(abortSignal.reason as Error);
            },
            { once: true },
        );
    });
}

function throwIfAborted(abortSignal: AbortSignal): void {
    if (abortSignal.aborted) {
        throw (abortSignal.reason as Error | undefined) ?? new Error("Aborted");
    }
}

/**
 * Download every file of every listed drawing's tile set into OPFS, skipping
 * whatever incremental caching (or a prior full sync) already journaled —
 * the same journal-union dedupe the SDK uses to skip incrementally-cached
 * geometry (RFD00000145).
 *
 * Best-effort: a drawing with no completed import yet, or a file that
 * exhausts its retries, is logged and skipped — this never throws for those
 * reasons, so one bad sheet can't fail the whole download. The one exception
 * is `abortSignal` firing (a user-initiated pause), which propagates so the
 * caller can tell "paused" apart from "finished, some drawings skipped".
 */
export async function syncMinimapFiles({
    scene,
    fileInstanceIds,
    resolveBaseUrl,
    abortSignal,
    onProgress,
}: {
    scene: MinimapFullSyncScene;
    fileInstanceIds: readonly string[];
    resolveBaseUrl: ResolveMinimapBaseUrl;
    abortSignal: AbortSignal;
    onProgress?: (current: number, total: number) => void;
}): Promise<void> {
    throwIfAborted(abortSignal);

    const existing = new Set<string>();
    for (const entry of await scene.dir.getJournalEntries()) {
        existing.add(entry.name);
    }

    // Resolve every drawing's manifest first (a ~1 KB document each), but
    // don't materialize the file list off them: a 20-sheet model is ~34,000
    // paths, several MB of strings held before a single byte is downloaded.
    // The generator below keeps only the current drawing's paths alive.
    const tileSets: { fileInstanceId: string; source: TiledDrawingSource; baseUrl: string }[] = [];
    for (const fileInstanceId of new Set(fileInstanceIds)) {
        throwIfAborted(abortSignal);
        const resolved = await resolveTileSetForFullSync(scene, fileInstanceId, resolveBaseUrl, existing, abortSignal);
        if (!resolved) {
            continue; // No completed import yet, or resolution failed -- telemetry already recorded.
        }
        tileSets.push({ fileInstanceId, ...resolved });
    }

    function* pendingDownloads(): Generator<DownloadItem> {
        for (const { fileInstanceId, source, baseUrl } of tileSets) {
            for (const relativePath of allTileSetFilePaths(source)) {
                const path = minimapFilePath(fileInstanceId, relativePath);
                if (path && !existing.has(path)) {
                    yield { fileInstanceId, relativePath, baseUrl };
                }
            }
        }
    }

    // Counted by a throwaway pass rather than tracked while enumerating: the
    // total has to be post-dedupe for the progress bar to stay honest on a
    // resumed sync, and deciding that means building each path to test it
    // against `existing`. Pure string work, no IO, and still only one
    // drawing's paths resident at a time.
    let total = 0;
    const counter = pendingDownloads();
    while (!counter.next().done) {
        total++;
    }

    let completed = 0;
    onProgress?.(completed, total);
    if (total === 0) {
        return;
    }

    const queue = pendingDownloads();
    async function worker(): Promise<void> {
        for (;;) {
            throwIfAborted(abortSignal);
            // Pulled synchronously, so two workers can never be handed the
            // same item however their awaits interleave.
            const next = queue.next();
            if (next.done) {
                return;
            }
            await downloadFileWithRetry(scene, next.value, resolveBaseUrl, abortSignal);
            completed++;
            onProgress?.(completed, total);
        }
    }

    const workerCount = Math.min(MAX_CONCURRENT_DOWNLOADS, total);
    // `allSettled`, not `all`: `downloadFileWithRetry` only ever rejects via
    // `throwIfAborted` (it's best-effort otherwise), so every rejection here
    // is abort-shaped -- but `Promise.all` would settle on the FIRST one and
    // leave the other workers' later rejections unhandled (an
    // `unhandledrejection` in the WebView, plus writes racing past the point
    // the caller already decided "aborted"). Letting every worker actually
    // finish observing the same abort, then re-throwing once, keeps pause
    // deterministic.
    await Promise.allSettled(Array.from({ length: workerCount }, worker));
    throwIfAborted(abortSignal);
}

/**
 * The tile set for one drawing, from OPFS if already journaled (fully
 * offline-capable, and dedupes a manifest re-fetch across repeated syncs),
 * else resolved online and teed into OPFS. `undefined` on any failure
 * (recorded via telemetry) or when the drawing has no completed import yet.
 *
 * The returned `baseUrl` is for downloading whatever tiles/previews are
 * still missing, which is why it's resolved even when the manifest itself
 * was already cached: a manifest being journaled doesn't mean every tile is
 * (e.g. incremental caching only ever wrote the ones actually viewed). Only
 * skipped (`baseUrl: ""`, never used) when this drawing's tile set is
 * already fully present, so a fully-cached re-sync needs no signed-URL call
 * at all.
 */
async function resolveTileSetForFullSync(
    scene: MinimapFullSyncScene,
    fileInstanceId: string,
    resolveBaseUrl: ResolveMinimapBaseUrl,
    existing: ReadonlySet<string>,
    abortSignal: AbortSignal,
): Promise<{ source: TiledDrawingSource; baseUrl: string } | undefined> {
    const manifestPath = minimapFilePath(fileInstanceId, TILE_MANIFEST_RELATIVE_PATH);
    let cachedSource: TiledDrawingSource | undefined;

    if (manifestPath && existing.has(manifestPath)) {
        try {
            const buffer = await scene.dir.read(manifestPath);
            if (buffer) {
                cachedSource = parseTileSetManifest(JSON.parse(new TextDecoder().decode(buffer)), "", manifestPath);
            }
        } catch (error) {
            l.warn("Failed to read cached minimap manifest, re-resolving online:", fileInstanceId, error);
        }
    }

    if (cachedSource) {
        const source = cachedSource;
        const allCached = allTileSetFilePaths(source).every((relativePath) => {
            const path = minimapFilePath(fileInstanceId, relativePath);
            // An unsafe path can never be downloaded either way -- don't let
            // it force a signed-URL resolve that would go unused.
            return path ? existing.has(path) : true;
        });
        if (allCached) {
            return { source, baseUrl: "" };
        }
    }

    const baseUrl = await resolveBaseUrl(fileInstanceId);
    if (!baseUrl) {
        l.debug("Skipping full-sync minimap download: no completed tile-set import yet:", fileInstanceId);
        return undefined;
    }

    if (cachedSource) {
        return { source: cachedSource, baseUrl };
    }

    const manifestUrl = buildTileSetFileUrl(baseUrl, TILE_MANIFEST_RELATIVE_PATH);
    try {
        const response = await fetch(manifestUrl, { signal: abortSignal });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status} for ${manifestUrl}`);
        }
        const text = await response.text();
        if (manifestPath) {
            // Best-effort: a failed tee just means this manifest re-fetches next time.
            await scene.dir.write(manifestPath, new TextEncoder().encode(text).buffer as ArrayBuffer).catch(() => {});
        }
        return { source: parseTileSetManifest(JSON.parse(text), baseUrl, manifestUrl), baseUrl };
    } catch (error) {
        // An abort mid-fetch must propagate, not be recorded as a resolution
        // failure and swallowed -- same reasoning as `downloadFileWithRetry`.
        throwIfAborted(abortSignal);
        telemetry.trackCaughtException({
            error,
            source: "miniMap.offlineSync.resolveTileSet",
            category: "data",
            severity: "warning",
            extraAttributes: { fileInstanceId },
        });
        return undefined;
    }
}

async function downloadFileWithRetry(
    scene: MinimapFullSyncScene,
    item: DownloadItem,
    resolveBaseUrl: ResolveMinimapBaseUrl,
    abortSignal: AbortSignal,
): Promise<void> {
    const path = minimapFilePath(item.fileInstanceId, item.relativePath);
    if (!path) {
        return;
    }
    // Mutable, not `item.baseUrl` directly: a 401/403 mints a fresh prefix
    // below and later attempts (for this file) retry against it.
    let baseUrl = item.baseUrl;
    for (let attempt = 0; ; attempt++) {
        throwIfAborted(abortSignal);
        try {
            const response = await fetch(buildTileSetFileUrl(baseUrl, item.relativePath), { signal: abortSignal });
            if (response.status === 401 || response.status === 403) {
                // Same "signature can outlive the session" case the online
                // route handles (`fetchTileSetFile` + `refreshTileSetBaseUrl`)
                // -- a full sync's hundreds of tiles across 8 workers, running
                // after geometry sync already finished, are the case most
                // likely to actually hit it. `null` (refresh failed) falls
                // through to the normal retry against the same stale prefix,
                // which will keep failing the same way until retries exhaust.
                const refreshed = await resolveBaseUrl(item.fileInstanceId, { forceRefresh: true });
                if (refreshed) {
                    baseUrl = refreshed;
                }
            }
            if (!response.ok) {
                throw new Error(`HTTP ${response.status} for ${item.relativePath}`);
            }
            const buffer = await response.arrayBuffer();
            await scene.dir.write(path, buffer);
            return;
        } catch (error) {
            // An abort mid-fetch (or on the final attempt) must propagate, not
            // be telemetried and swallowed as a routine best-effort skip --
            // that would silently turn a user-initiated pause into "this file
            // just failed" from the caller's point of view.
            throwIfAborted(abortSignal);
            if (attempt >= RETRY_BACKOFF_MS.length) {
                telemetry.trackCaughtException({
                    error,
                    source: "miniMap.offlineSync.downloadFile",
                    category: "data",
                    severity: "warning",
                    extraAttributes: { fileInstanceId: item.fileInstanceId, relativePath: item.relativePath },
                });
                return;
            }
            await delay(RETRY_BACKOFF_MS[attempt], abortSignal);
        }
    }
}
